import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import useApplicationDetail from './useApplicationDetail';
import CustomTable from 'components/CustomTable';
import {
    APPLICANT,
    CCO,
    CLUSTER_ADMIN,
    IRB,
    SECRETARY,
    REVIEWER,
    HO_ADMIN,
    DRAFT,
    getApplicantStatusDisplay,
    RETURNED,
    roleDisplayLookup,
    statusKeyToDisplay,
} from 'utility/lookup';
// import useApplicationBarAction from './useApplicationBarAction';
import useApplicationTableAction from 'hooks/useApplicationTableAction';
import MenuAction from 'components/MenuAction';
import SitePopup from 'components/SitePopup';
import { getTimeWithTimeZone, isPendingCheck } from 'utility/general';
import { useDispatch } from 'react-redux';
import { APPLICATION_FETCH } from 'store/key';
import { KEYS, setItem } from 'storage';

const MenuActionWithApplicationData = ({ row, siteResponseList }) => {
    let applicationTableActions = useApplicationTableAction({ row, siteResponseList }).filter((item) => {
        if (item.condition) {
            return item.condition();
        }

        return true;
    });

    if (applicationTableActions.length <= 0) {
        return null;
    }

    return (
        <MenuAction actions={applicationTableActions} row={row} checkBackEndStatus={true} documentType={'Initail'} />
    );
};

const useApplicationTableData = ({ folder, onSortFunc }) => {
    const { columns: postApprovalColumns } = useApplicationDetail();
    // const { columns: postAp}
    const rows = useSelector((state) => state.application.data);
    const history = useHistory();
    const columnToDisplay = useSelector((state) => state.column.application);
    const dispatch = useDispatch();
    const pageSize = useSelector((state) => state.application.pageSize);
    const page = useSelector((state) => state.application.page);
    const user = useSelector((state) => state.user);

    const fetchApplication = (key, isDesc = true) => {
        if (onSortFunc) {
            onSortFunc(null, null, key, isDesc);
        } else {
            const requestParams = {
                isDesc,
                page: 1,
                pageSize,
                sort: key,
            };

            if (folder !== 'all') {
                requestParams.folder = folder;
            }

            dispatch({
                type: APPLICATION_FETCH,
                payload: requestParams,
            });
        }
    };

    const rawColumn = [
        {
            field: 'pending',
            headerName: 'Pending',
            mandatory: true,
            disableResize: true,
            width: 70,
            fixWidth: true,
            disableInColumnSelect: true,
            disableItemSort: true,
            hiddenInRoles: [CCO, CLUSTER_ADMIN, IRB, SECRETARY, HO_ADMIN], // hide this column when it is not applicant role
            renderCell: (params) => {
                const isPending = isPendingCheck(params.row, user);

                if (!isPending) {
                    return <div>-</div>;
                }

                return (
                    <div style={{ height: 24 }}>
                        <ErrorOutlineIcon style={{ color: '#DC143C' }} />
                    </div>
                );
            },
        },
        { field: 'id', headerName: 'ID', width: 150 },
        {
            field: 'appNo',
            headerName: 'CRER Application No.',
            width: 150,
            mandatory: false,
            defaultValue: '-',
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'refNo',
            headerName: 'IRB/REC Reference No.',
            width: 150,
            mandatory: false,
            defaultValue: '-',
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'subNo',
            headerName: 'Submission No.',
            width: 150,
            mandatory: false,
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'p1scientificTitle',
            headerName: 'Scientific Title',
            width: 250,
            dotNotion: true,
            mandatory: true,
            textNotWrap: true,
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
            goToPath: (params) => {
                setItem(KEYS.APP_PAGE, page);
                setItem(KEYS.APP_PAGE_SIZE, pageSize);
                history.push(`/applications/initial-application-form?id=${params.row.id}`);
            },
            hoverComponent: (params) => {
                return (
                    <div style={{ padding: '0 5px', maxWidth: 400, lineHeight: 1.4 }}>
                        {params.row.p1scientificTitle}
                    </div>
                );
            },
        },
        {
            field: 'coordinator',
            headerName: 'Coordinating Investigator (CI)',
            width: 150,
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'coordinatorHospital',
            headerName: 'CI Hospital/Institution',
            width: 150,
            sortKey: 'institution',
            onSort: (key, isDesc) => {
                fetchApplication('institution', isDesc);
            },
        },
        {
            field: 'statusId',
            headerName: 'Status',
            mandatory: true,
            width: 100,
            disableItemSort: true,
            filterValue: (params) => {
                if (user.role === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.row.statusId);
            },
            valueGetter: (params) => {
                if (user.role === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.value);
            },
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'siteResponseList',
            headerName: 'Site Info',
            width: 120,
            mandatory: false,
            filterValue: (params) => {
                const { siteResponseList } = params.row;

                const isAllComplete = siteResponseList.every((item) => item.completed);

                if (isAllComplete) {
                    return 'Ready to submit';
                } else {
                    return 'Incomplete';
                }
            },
            renderCell: (params) => {
                const { complete, statusId, siteResponseList, id } = params.row;

                const editStatus = statusId === DRAFT || statusId === RETURNED;

                // if (!editStatus) {
                //     return '-';
                // }

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {editStatus && <div>{complete}</div>}
                        <div style={{ marginLeft: 5 }}>
                            <SitePopup
                                formId={id}
                                status={statusId}
                                data={siteResponseList}
                                showPauseIcon={!editStatus}
                            />
                        </div>
                    </div>
                );

                // if (statusId === DRAFT || statusId === RETURNED) {

                // } else {
                //     return '-';
                // }
            },
            disableItemSort: true,
        },
        {
            field: 'updateDtm',
            headerName: 'Last Modified Date',
            width: 160,
            mandatory: false,
            filterValue: (params) => {
                return getTimeWithTimeZone(params.row.updateDtm).format('YYYY-MM-DD HH:mm:ss');
            },
            valueGetter: (params) => {
                return getTimeWithTimeZone(params.row.updateDtm).format('YYYY-MM-DD HH:mm:ss');
            },
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'expiryDtm',
            headerName: 'Expiry Date',
            width: 120,
            disableItemSort: user.role === REVIEWER, // disable sorting when the user role is reviewer to prevent error occur
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
            valueGetter: (params) => {
                if (params.row.statusId !== RETURNED) {
                    return '-';
                }

                const expiry = params.row.expiryDtm;

                if (!expiry) {
                    return '-';
                }

                return getTimeWithTimeZone(expiry).format('YYYY-MM-DD');
            },
        },
        {
            field: 'lastHandledBy',
            headerName: 'Last Handled by',
            width: 120,
            sortKey: 'lastHandledBy',
            mandatory: false,
            hiddenInRoles: [APPLICANT],
            valueGetter: (params) => {
                if (!params.value) {
                    return '-';
                }

                return roleDisplayLookup[params.value];
            },
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },

        {
            field: 'versionNo',
            headerName: 'Version No.',
            width: 80,
            valueGetter: (params) => {
                return params.value;
            },
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
        },
        {
            field: 'singleCluster',
            headerName: 'Single/Multi-Cluster',
            width: 120,
            onSort: (key, isDesc) => {
                fetchApplication(key, isDesc);
            },
            filterValue: (params) => {
                if (params.row.singleCluster === true) {
                    return 'Single';
                } else if (params.row.singleCluster === false) {
                    return 'Multiple';
                } else {
                    return '-';
                }
            },
            disableItemSort: true,
            valueGetter: (params) => {
                if (params.row.singleCluster === true) {
                    return 'Single';
                } else if (params.row.singleCluster === false) {
                    return 'Multiple';
                } else {
                    return '-';
                }
            },
        },
        {
            field: 'subTable',
            hiddenInTableColumn: true,
            getIsToggleEnable: (params) => {
                const { postApprovalDocumentResults } = params.row;
                if (postApprovalDocumentResults.length === 0) {
                    return false;
                }

                return true;
            },
            subTable: (params) => {
                const { postApprovalDocumentResults } = params.row;

                return (
                    <div style={{ padding: 15, paddingLeft: 30, position: 'relative' }}>
                        <CustomTable
                            isSubTable
                            disableTableBorder
                            headerBackground={'#efeff2'}
                            fullWidthTable={false}
                            showAlternativeBackground={false}
                            isScrollable={false}
                            rows={postApprovalDocumentResults}
                            columns={postApprovalColumns}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                left: 15,
                                top: 0,
                                width: 2,
                                height: 30,
                                background: '#808080',
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                left: 15,
                                top: 30,
                                width: 15,
                                height: 2,
                                background: '#808080',
                            }}
                        />

                        <div
                            style={{
                                position: 'absolute',
                                left: 21,
                                top: 26,
                                width: 0,
                                height: 0,
                                borderStyle: 'solid',
                                borderWidth: '5px 0 5px 10px',
                                borderColor: 'transparent transparent transparent #808080',
                            }}
                        />
                    </div>
                );
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 80,
            disableItemSort: true,
            disableInColumnSelect: true,
            disableResize: true,
            mandatory: true,
            renderCell: (params) => {
                return (
                    <MenuActionWithApplicationData row={params.row} siteResponseList={params.row.siteResponseList} />
                );
            },
        },
    ];

    const filteredColumn = rawColumn
        .filter((item) => {
            const isExist = columnToDisplay.some((displayColumn) => {
                return displayColumn.key === item.field;
            });

            return isExist;
        })
        .filter((item) => {
            if (item?.hiddenInRoles?.includes(user.role)) {
                return false;
            }

            return true;
        });
    return { columns: filteredColumn, rawColumn: rawColumn, rows: rows || [] };
};

export default useApplicationTableData;
